import React, { useState, useRef, useEffect } from 'react';
import {
  AppBar,
  Box,
  Button,
  Container,
  Drawer,
  IconButton,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Toolbar,
  Typography,
  Divider,
  Menu,
  MenuItem,
  Collapse,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import CloseIcon from '@mui/icons-material/Close';
import MenuIcon from '@mui/icons-material/Menu';
import HomeIcon from '@mui/icons-material/Home';
import InfoIcon from '@mui/icons-material/Info';
import ArticleIcon from '@mui/icons-material/Article';
import MiscellaneousServicesIcon from '@mui/icons-material/MiscellaneousServices';
import HelpIcon from '@mui/icons-material/Help';
import ContactMailIcon from '@mui/icons-material/ContactMail';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import MonetizationOnIcon from '@mui/icons-material/MonetizationOn';
import PeopleIcon from '@mui/icons-material/People';
import HomeWorkIcon from '@mui/icons-material/HomeWork';
import AssessmentIcon from '@mui/icons-material/Assessment';
import FolderIcon from '@mui/icons-material/Folder';
import CampaignIcon from '@mui/icons-material/Campaign';
import EngineeringIcon from '@mui/icons-material/Engineering';

// Import logo
import navbarLogo from '../images/lantena_white_navbar.png';

// Define service items with paths and icons
const SERVICE_ITEMS = [
  { 
    label: 'Rent Collection & Accounting', 
    path: '/rent-collection', 
    icon: <MonetizationOnIcon sx={{ color: '#a6d1ea' }} /> 
  },
  { 
    label: 'Tenant Management', 
    path: '/tenant-management', 
    icon: <PeopleIcon sx={{ color: '#a6d1ea' }} /> 
  },
  { 
    label: 'Property Management', 
    path: '/property-management', 
    icon: <HomeWorkIcon sx={{ color: '#a6d1ea' }} /> 
  },
  { 
    label: 'Assessment of Rent & Service Charges', 
    path: '/assessment', 
    icon: <AssessmentIcon sx={{ color: '#a6d1ea' }} /> 
  },
  { 
    label: 'MRI & Filing', 
    path: '/mri-filing', 
    icon: <FolderIcon sx={{ color: '#a6d1ea' }} /> 
  },
  { 
    label: 'Property Marketing', 
    path: '/property-marketing', 
    icon: <CampaignIcon sx={{ color: '#a6d1ea' }} /> 
  },
  { 
    label: 'Project Management', 
    path: '/project-management', 
    icon: <EngineeringIcon sx={{ color: '#a6d1ea' }} /> 
  },
];

const Navbar = () => {
  const theme = useTheme();
  const location = useLocation();
  const navigate = useNavigate();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  
  // State for the navbar
  const [mobileOpen, setMobileOpen] = useState(false);
  const [servicesAnchorEl, setServicesAnchorEl] = useState(null);
  const servicesOpen = Boolean(servicesAnchorEl);
  
  // State for logo press functionality
  const logoRef = useRef(null);
  const [pressTimer, setPressTimer] = useState(null);
  
  // Navigation items
  const navItems = [
    { label: 'Home', path: '/' },
    { label: 'About Us', path: '/about-us' },
    { label: 'Community Blog', path: '/community-blog' },
    { label: 'Services', path: '/services' },
    { label: 'FAQ', path: '/faq' },
    { label: 'Contact', path: '/contact' },
  ];

  // Check if the current path matches the nav item path
  const isActive = (path) => {
    return location.pathname === path;
  };

  // Check if any service path is active
  const isServiceActive = () => {
    return SERVICE_ITEMS.some(service => isActive(service.path));
  };

  // Handle drawer toggle
  const handleDrawerToggle = () => {
    console.log("Mobile drawer toggle", mobileOpen ? "closing" : "opening");
    setMobileOpen(prevState => !prevState);
  };

  // Handle services dropdown menu
  const handleServicesClick = (event) => {
    console.log("Services clicked", event.currentTarget);
    setServicesAnchorEl(event.currentTarget);
  };

  const handleServicesClose = () => {
    setServicesAnchorEl(null);
  };

  // Handle logo functionality (for admin access)
  const handleLogoDoubleClick = () => {
    navigate('/admin-login');
  };

  const handleLogoMouseDown = () => {
    const timer = setTimeout(() => {
      navigate('/admin-login');
    }, 1000);
    setPressTimer(timer);
  };

  const handleLogoMouseUp = () => {
    if (pressTimer) {
      clearTimeout(pressTimer);
      setPressTimer(null);
    }
  };

  // Clean up timer on unmount
  useEffect(() => {
    return () => {
      if (pressTimer) {
        clearTimeout(pressTimer);
      }
    };
  }, [pressTimer]);

  // Mobile drawer component
  const MobileDrawer = () => {
    // State to control the services submenu in the mobile drawer
    const [servicesSubOpen, setServicesSubOpen] = useState(false);
  
    // Toggle services submenu
    const handleServicesToggle = () => {
      console.log("Toggling services submenu in mobile drawer");
      setServicesSubOpen(!servicesSubOpen);
    };
    
    // Get icons for each navigation item
  const getIcon = (label) => {
    switch (label) {
      case 'Home':
        return <HomeIcon sx={{ mr: 2, color: '#a6d1ea' }} />;
      case 'About Us':
        return <InfoIcon sx={{ mr: 2, color: '#a6d1ea' }} />;
      case 'Community Blog':
        return <ArticleIcon sx={{ mr: 2, color: '#a6d1ea' }} />;
      case 'Services':
        return <MiscellaneousServicesIcon sx={{ mr: 2, color: '#a6d1ea' }} />;
      case 'FAQ':
        return <HelpIcon sx={{ mr: 2, color: '#a6d1ea' }} />;
      case 'Contact':
        return <ContactMailIcon sx={{ mr: 2, color: '#a6d1ea' }} />;
      default:
        return null;
    }
  };

  return (
    <Drawer
        variant="temporary"
        open={mobileOpen}
        onClose={handleDrawerToggle}
      anchor="right"
      ModalProps={{
        keepMounted: true,
          style: { zIndex: 9999 }
        }}
        SlideProps={{
          style: { zIndex: 9999 }
        }}
        sx={{ 
          zIndex: 9999,
          '& .MuiBackdrop-root': {
            zIndex: 9998
          }
      }}
      PaperProps={{
        sx: {
          width: 280,
          backgroundColor: '#0a233b',
            color: '#fbfbfb',
            zIndex: 9999,
        }
      }}
    >
      <Box sx={{ p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <img src={navbarLogo} alt="Lantena Logo" style={{ height: 40 }} />
          <IconButton onClick={handleDrawerToggle} sx={{ color: '#fbfbfb' }}>
          <CloseIcon />
        </IconButton>
      </Box>
      
      <Divider sx={{ backgroundColor: 'rgba(166, 209, 234, 0.1)', my: 1 }} />
      
      <List sx={{ px: 1 }}>
        {navItems.map((item) => (
            item.label === 'Services' ? (
              <React.Fragment key={item.label}>
                <ListItem
                  onClick={handleServicesToggle}
                  sx={{ 
                    py: 2,
                    my: 0.5,
                    color: servicesSubOpen || isServiceActive() ? '#f66b10' : '#fbfbfb',
                    cursor: 'pointer',
                    borderRadius: '8px',
                    backgroundColor: servicesSubOpen ? 'rgba(166, 209, 234, 0.1)' : 'transparent',
                    '&:hover': { 
                      backgroundColor: 'rgba(166, 209, 234, 0.05)',
                    }
                  }}
                >
                  {getIcon(item.label)}
                  <ListItemText primary={item.label} />
                  {servicesSubOpen ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
                </ListItem>
                
                <Collapse in={servicesSubOpen} timeout="auto" unmountOnExit>
                  <List component="div" disablePadding>
                    {SERVICE_ITEMS.map((service) => (
                      <ListItem
                        key={service.label}
                        component={Link}
                        to={service.path}
                        onClick={handleDrawerToggle}
                        sx={{ 
                          pl: 4,
                          py: 1,
                          my: 0.5,
                          color: isActive(service.path) ? '#f66b10' : '#fbfbfb',
                          cursor: 'pointer',
                          borderRadius: '8px',
                          backgroundColor: isActive(service.path) ? 'rgba(166, 209, 234, 0.1)' : 'transparent',
                          '&:hover': { 
                            backgroundColor: 'rgba(166, 209, 234, 0.05)',
                            transform: 'translateX(5px)',
                            transition: 'transform 0.2s ease-in-out'
                          }
                        }}
                      >
                        <ListItemIcon sx={{ minWidth: 36 }}>
                          {service.icon}
                        </ListItemIcon>
                        <ListItemText 
                          primary={service.label} 
                          primaryTypographyProps={{
                            fontSize: '0.85rem',
                            fontWeight: isActive(service.path) ? 600 : 400,
                          }}
                        />
                      </ListItem>
                    ))}
                  </List>
                </Collapse>
              </React.Fragment>
            ) : (
          <ListItem 
            key={item.label}
            component={Link}
            to={item.path}
                onClick={handleDrawerToggle}
            sx={{ 
              borderLeft: isActive(item.path) ? '4px solid #f66b10' : '4px solid transparent',
              py: 2,
              my: 0.5,
              color: isActive(item.path) ? '#f66b10' : '#fbfbfb',
              cursor: 'pointer',
              borderRadius: '0 8px 8px 0',
              backgroundColor: isActive(item.path) ? 'rgba(166, 209, 234, 0.1)' : 'transparent',
              '&:hover': { 
                backgroundColor: 'rgba(166, 209, 234, 0.05)',
                transform: 'translateX(5px)',
                transition: 'transform 0.2s ease-in-out'
              }
            }}
          >
            {getIcon(item.label)}
            <ListItemText 
              primary={item.label} 
              primaryTypographyProps={{
                fontWeight: isActive(item.path) ? 600 : 400,
              }}
            />
          </ListItem>
            )
        ))}
      </List>
      
      <Box sx={{ mt: 'auto', p: 2, textAlign: 'center' }}>
        <Divider sx={{ backgroundColor: 'rgba(166, 209, 234, 0.1)', mb: 2 }} />
        <Box sx={{ color: '#a6d1ea', fontSize: '0.9rem' }}>
          © {new Date().getFullYear()} Lantena Property Management
        </Box>
      </Box>
    </Drawer>
  );
};

  return (
    <Box sx={{ position: 'relative', zIndex: 1200 }}>
      {/* App Bar */}
      <AppBar position="fixed" sx={{ backgroundColor: '#0a233b', zIndex: 1201 }}>
        <Container maxWidth="lg">
          <Toolbar sx={{ justifyContent: 'space-between', padding: isMobile ? '0.5rem 0' : '0.5rem 1rem' }}>
            {/* Logo */}
            <Box
              component={Link}
              to="/"
              ref={logoRef}
              sx={{ display: 'flex' }}
              onDoubleClick={handleLogoDoubleClick}
              onMouseDown={handleLogoMouseDown}
              onMouseUp={handleLogoMouseUp}
              onMouseLeave={handleLogoMouseUp}
            >
              <img src={navbarLogo} alt="Lantena Logo" style={{ height: 50 }} />
            </Box>

            {/* Desktop Navigation */}
            {!isMobile && (
              <Box sx={{ display: 'flex' }}>
                {navItems.map((item) => (
                  item.label === 'Services' ? (
                    <Box key={item.label} sx={{ position: 'relative' }}>
                      <Button
                        id="services-button"
                        aria-controls={servicesOpen ? 'services-menu' : undefined}
                        aria-haspopup="true"
                        aria-expanded={servicesOpen ? 'true' : undefined}
                        onClick={handleServicesClick}
                        endIcon={servicesOpen ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
                        sx={{
                          textTransform: 'none',
                          color: isServiceActive() ? '#f66b10' : '#a6d1ea',
                          borderBottom: isServiceActive() ? '2px solid #f66b10' : 'none',
                          borderRadius: 0,
                          mx: 1,
                          py: 1,
                          '&:hover': { color: '#f66b10' }
                        }}
                      >
                        {item.label}
                      </Button>
                      <Menu
                        id="services-menu"
                        anchorEl={servicesAnchorEl}
                        open={servicesOpen}
                        onClose={handleServicesClose}
                        MenuListProps={{
                          'aria-labelledby': 'services-button',
                        }}
                        disablePortal
                        container={document.body}
                        sx={{ zIndex: 9999 }}
                        PopoverClasses={{
                          root: {
                            pointerEvents: 'none',
                          },
                          paper: {
                            pointerEvents: 'auto',
                          },
                        }}
                        anchorOrigin={{
                          vertical: 'bottom',
                          horizontal: 'left',
                        }}
                        transformOrigin={{
                          vertical: 'top',
                          horizontal: 'left',
                        }}
                        PaperProps={{
                          style: {
                            backgroundColor: '#0a233b',
                            borderRadius: 8,
                            minWidth: 280,
                            marginTop: 8,
                            boxShadow: '0 5px 15px rgba(0,0,0,0.3)',
                            border: '1px solid rgba(166, 209, 234, 0.2)',
                            zIndex: 9999
                          }
                        }}
                      >
                        {SERVICE_ITEMS.map((service) => (
                          <MenuItem 
                            key={service.label}
                            component={Link}
                            to={service.path}
                            onClick={handleServicesClose}
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              color: isActive(service.path) ? '#f66b10' : '#fbfbfb',
                              py: 1.2,
                              px: 2,
                              borderRadius: 1,
                              '&:hover': { 
                                backgroundColor: 'rgba(166, 209, 234, 0.1)',
                                color: '#f66b10'
                              }
                            }}
                          >
                            <Box sx={{ mr: 2, display: 'flex', alignItems: 'center' }}>
                              {service.icon}
                            </Box>
                            <Typography variant="body1">
                              {service.label}
                            </Typography>
                          </MenuItem>
                        ))}
                      </Menu>
                    </Box>
                  ) : (
                  <Button
                    key={item.label}
                    component={Link}
                    to={item.path}
                    sx={{
                      textTransform: 'none',
                      color: isActive(item.path) ? '#f66b10' : '#a6d1ea',
                      borderBottom: isActive(item.path) ? '2px solid #f66b10' : 'none',
                      borderRadius: 0,
                      mx: 1,
                        py: 1,
                      '&:hover': { color: '#f66b10' }
                    }}
                  >
                    {item.label}
                  </Button>
                  )
                ))}
              </Box>
            )}

            {/* Mobile Menu Button */}
            {isMobile && (
              <IconButton
                edge="end"
                color="inherit"
                aria-label="menu"
                onClick={handleDrawerToggle}
                sx={{ 
                  color: '#fbfbfb',
                  border: '1px solid rgba(166, 209, 234, 0.3)',
                  '&:hover': {
                    backgroundColor: 'rgba(166, 209, 234, 0.1)',
                  }
                }}
              >
                <MenuIcon />
              </IconButton>
            )}
          </Toolbar>
        </Container>
      </AppBar>
      
      {/* Toolbar placeholder to prevent content from hiding under the app bar */}
      <Toolbar id="navbar-spacer" />

      {/* Mobile Drawer */}
      <MobileDrawer />
    </Box>
  );
};

export default Navbar;